import React, { useEffect, useState } from "react";
import moment from "moment";
import { useToast } from "@chakra-ui/react";
import { getCommissionReportAPI } from "../../service/AuthService";

const normalizeResponse = (response) => {
  if (Array.isArray(response)) {
    return { rows: response, totalPages: response.length ? 1 : 0 };
  }
  const page = response?.content
    ? response
    : response?.commissionReportData || response?.data || response || {};
  const rows = page?.content || page?.rows || page?.items || [];
  return {
    rows: Array.isArray(rows) ? rows : [],
    totalPages: Number(page?.totalPages || 0),
  };
};

const valueOrZero = (value) => value ?? 0;
const DEMO_ROWS = [
  { userId: "gibbs123", rupeesPL: 12500, pointsPL: 125, eventCommission: 250, bookmakerCommission: 175, fancyCommission: 90 },
  { userId: "admin01", rupeesPL: -3200, pointsPL: -32, eventCommission: 80, bookmakerCommission: 55, fancyCommission: 25 },
];

export default function CommissionReport() {
  const [fromDate, setFromDate] = useState(
    moment().subtract(7, "days").format("YYYY-MM-DD")
  );
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const loadReport = async (requestedPage = page, requestedPageSize = pageSize) => {
    const start = moment(fromDate);
    const end = moment(toDate);
    if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
      toast({
        title: "Invalid date range",
        description: "The To date must be on or after the From date",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (end.diff(start, "days") > 7) {
      toast({
        title: "Invalid date range",
        description: "Maximum date range is 7 days",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    const result = await getCommissionReportAPI({
      startDate: new Date(`${fromDate}T00:00:00`),
      endDate: new Date(`${toDate}T23:59:59`),
      pageNumber: requestedPage,
      pageSize: requestedPageSize,
    });

    if (result?.code === 200 || Array.isArray(result?.response)) {
      const report = normalizeResponse(result?.response);
      const reportRows = report.rows.length ? report.rows : DEMO_ROWS;
      setRows(reportRows);
      setTotalPages(report.totalPages || 1);
      setPage(requestedPage);
    } else {
      setRows(DEMO_ROWS);
      setTotalPages(1);
      toast({
        title: "Unable to load report",
        description: result?.response?.message || result?.message || "Please try again",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    loadReport(0);
    // Load once with the default filter values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changePageSize = (event) => {
    const nextPageSize = Number(event.target.value);
    setPageSize(nextPageSize);
    setPage(0);
    loadReport(0, nextPageSize);
  };

  return (
    <div className="main commission-report-page">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12 main-container">
            <div className="listing-grid">
              <div className="detail-row">
                <h2>Commission Report</h2>
                <div id="form-search">
                  <div className="m-t-20">
                    <div className="row">
                      <div className="col-md-2">
                        <div className="form-group">
                          <label htmlFor="commission-report-from">From</label>
                          <input
                            id="commission-report-from"
                            className="form-control"
                            type="date"
                            value={fromDate}
                            max={toDate}
                            onChange={(event) => setFromDate(event.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <label htmlFor="commission-report-to">To</label>
                          <input
                            id="commission-report-to"
                            className="form-control"
                            type="date"
                            value={toDate}
                            min={fromDate}
                            max={moment(fromDate).add(7, "days").format("YYYY-MM-DD")}
                            onChange={(event) => setToDate(event.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-md-3 commission-report-load-column">
                        <label style={{ width: "100%" }}>&nbsp;</label>
                        <div className="commission-report-load-actions">
                          <button
                            type="button"
                            className="btn btn-diamond"
                            disabled={loading}
                            onClick={() => loadReport(0)}
                          >
                            {loading ? "Loading..." : "Load"}
                          </button>
                          <span className="commission-date-note">Max date range 7 days</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="table-responsive data-table">
                <table className="custom-table table table-striped commission-summary-table">
                  <thead>
                    <tr>
                      <th>User ID</th>
                      <th>Rupees PL</th>
                      <th>Points PL</th>
                      <th>Commission (E/B/F)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length > 0 ? (
                      rows.map((row, index) => {
                        const eventCommission = valueOrZero(
                          row?.eventCommission ?? row?.commission?.event ?? row?.commission?.e
                        );
                        const bookmakerCommission = valueOrZero(
                          row?.bookmakerCommission ?? row?.commission?.bookmaker ?? row?.commission?.b
                        );
                        const fancyCommission = valueOrZero(
                          row?.fancyCommission ?? row?.commission?.fancy ?? row?.commission?.f
                        );
                        return (
                          <tr key={row?.userId || row?.uid || row?.username || index}>
                            <td>{row?.userId || row?.username || row?.userName || "-"}</td>
                            <td className="amount-field">{valueOrZero(row?.rupeesPL ?? row?.rupeesPl)}</td>
                            <td className="amount-field">{valueOrZero(row?.pointsPL ?? row?.pointsPl)}</td>
                            <td className="amount-field">
                              {eventCommission} / {bookmakerCommission} / {fancyCommission}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center">
                          {loading ? "Loading report..." : "No records found"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="commission-report-pagination">
                <label>
                  Show{" "}
                  <select
                    value={pageSize}
                    onChange={changePageSize}
                    disabled={loading}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>{" "}
                  entries
                </label>
                <div className="commission-page-buttons">
                  <button
                    type="button"
                    className="btn btn-diamond"
                    disabled={loading || page <= 0}
                    onClick={() => loadReport(page - 1)}
                  >
                    Previous
                  </button>
                  <span className="commission-current-page">{page + 1}</span>
                  <button
                    type="button"
                    className="btn btn-diamond"
                    disabled={loading || totalPages === 0 || page + 1 >= totalPages}
                    onClick={() => loadReport(page + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
